import { NgView } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { ApiService } from "@app/services/api.service";
import { AuthService } from "@app/services/auth.service";
import { DialogService } from "@app/services/dialog.service";
import { DrawerService } from "@app/services/drawer.service";
import { QuoteService } from "@app/services/quote.service";
import { BaseController, IBaseScope } from "@app/views/base.view";
import { eAlertType, IProduct, IRootScope, IVolumeDiscount } from "@models";
import { icons } from "@tools";
import { StateService } from "@uirouter/angularjs";

export interface IProductScope extends IBaseScope {
    model: IProduct;
    selectedMedia: string;
    add: () => void;
    selectMedia: (path: string) => void;
    qty: number;
    relatedProducts: IProduct[];
    search: () => void;
    addToQuote: () => void;
    getVolumeDiscountText: (volumeDiscount: IVolumeDiscount) => string;
}

@NgView({
    token: Token.ProductView,
    dependencies: [
        Token.$Scope,
        Token.$State,
        Token.$RootScope,
        Token.ApiService,
        Token.DrawerService,
        Token.QuoteService,
        Token.DialogService,
        Token.Model
    ],
    resolves: [
        {
            name: "model",
            dependencies: [
                Token.ApiService,
                Token.AuthService,
                Token.$StateParams,
                Token.$RootScope,
            ],
            fn: (
                api: ApiService,
                authService: AuthService,
                $stateParams: any,
                $rootScope: IRootScope
            ) => {
                return authService.authPromise.then(() =>
                    api.products.getById($stateParams.id, $rootScope.contentTracker)
                );
            }
        }
    ]
})
export class ProductController extends BaseController {

    constructor(
        $scope: IProductScope,
        $state: StateService,
        $rootScope: IRootScope,
        api: ApiService,
        drawerService: DrawerService,
        quoteService: QuoteService,
        dialogService: DialogService,
        model: IProduct
    ) {

        super();
        drawerService.setHelpUrl("Products");
        drawerService.pageTitle = $scope.model.name;
        drawerService.pageType = loc.product;
        drawerService.pageIcon = icons.standardProducts;

        $scope.model = model;
        $scope.qty = $scope.model.minQty;

        if (model.images && model.images.length > 0) {
            $scope.selectedMedia = model.images[0].imagePath;
        }

        $scope.selectMedia = (path: string) => {
            $scope.selectedMedia = path;
        };

        $scope.addToQuote = () => {
            quoteService.addProduct({ idProduct: $scope.model.id, qty: $scope.qty, addLocally: true });
        };

        $scope.getVolumeDiscountText = (volumeDiscount: IVolumeDiscount) => {
            let result = volumeDiscount.qty.toString();
            let nextTier: IVolumeDiscount = null; 
            let orderedVolumeDiscounts = $scope.model.volumeDiscounts.sort((a, b) => a.qty - b.qty);
            for (var i = 0; i < orderedVolumeDiscounts.length; i++) {
                if (orderedVolumeDiscounts[i].qty == volumeDiscount.qty) {
                    nextTier = orderedVolumeDiscounts[i + 1];
                    break;
                }
            }
            if (nextTier) {
                result += " " + loc.to + " " + (nextTier.qty - 1).toString() + " " + loc.units;
            }
            else {
                result += " " + loc.units + " " + loc.andabove + ": "; 
            }
            return result;
        }

        api.products.searchByCategory({
            query: (model.relatedProductsQuery ? model.relatedProductsQuery : model.name) + " -id:" + model.id,
            take: 5,
            skip: 0
        }).then(r => $scope.relatedProducts = r);

        // add drawers
        if (model.isConfigured) {
            let drConfigure = drawerService.addDrawer(
                icons.gear,
                "Configure",
                () => $state.go("kb.configurator", { id: model.id })
            );
        } else {
            let drAdd = drawerService.addDrawer(
                icons.add,
                loc.addtoquote,
                $scope.addToQuote
            );
        }

        if (!$rootScope.comparedProducts.contains($scope.model.id)) {
            drawerService.addDrawer(
                icons.cast,
                loc.compare,
                () => {
                    if ($rootScope.comparedProducts.length >= 4) {
                        dialogService.alert({
                            msg: loc.msg_productcomparisonlimit,
                            type: eAlertType.info
                        })
                    } else {
                        $rootScope.comparedProducts.push($scope.model.id);
                        $state.go("kb.products");
                    }
                }
            );
        }

    }
}
